<template>
  <div
    v-if="showRaffleModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
    @click.self="$emit('closeRaffleModal')"
  >
    <!-- Twister Spinner Overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 z-50 flex items-center justify-center bg-black/20"
    >
      <div class="twist-spinner w-[60px] h-[60px]"></div>
    </div>

    <!-- Modal Content -->
    <div
    v-else
      class="relative w-full max-w-md bg-[#121212] border border-[#00F0FF]/20 rounded-[16px] shadow-[0_0_30px_#00F0FF15] p-6 text-white text-center"
    >
      <!-- Close Button -->
      <button
        @click="$emit('closeRaffleModal')"
        class="absolute top-4 right-5 text-white text-3xl font-bold hover:text-[#00F0FF] transition"
      >
        &times;
      </button>

      <div class="mt-4 opacity-100" :class="{ 'opacity-20 pointer-events-none': loading }">
        <!-- Title -->
        <h2 class="text-2xl font-black text-[#00F0FF] uppercase tracking-wide mb-2">
          {{ isUserLoggedIn ? $t('Modal.RaffleEntry.EnterRaffle') : $t('Modal.RaffleEntry.LoginRequired') }}
        </h2>

        <!-- Not Logged In -->
        <div v-if="!isUserLoggedIn" class="mt-6">
          <p class="text-white/80">{{ $t('Modal.RaffleEntry.LoginRequiredText') }}</p>
          <button
            @click="handleLogin"
            class="mt-6 w-full px-6 py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#1ED6FF] transition-colors"
          >
            {{ $t('Modal.RaffleEntry.LogIn') }}
          </button>
        </div>

        <!-- Logged In -->
        <div v-else>
          <p class="text-white/90 text-sm leading-relaxed mt-4">
            {{ $t('Modal.RaffleEntry.HowManyEntries') }}
            <span class="text-[#00F0FF] font-bold">{{ raffleName }}</span>?
          </p>

          <!-- Entry Counter -->
          <div class="flex items-center justify-center gap-4 mt-5">
            <button
              @click="decreaseEntries"
              class="bg-[#1A1D24] text-white w-10 h-10 rounded-lg text-2xl font-bold hover:bg-[#2A2E38] transition border border-white/5"
            >
              −
            </button>
            <input
              type="number"
              v-model="entries"
              min="1"
              class="w-20 h-10 text-center bg-[#0D0B10] border border-[#00F0FF]/30 text-white font-bold rounded-lg"
            />
            <button
              @click="increaseEntries"
              class="bg-[#1A1D24] text-white w-10 h-10 rounded-lg text-2xl font-bold hover:bg-[#2A2E38] transition border border-white/5"
            >
              +
            </button>
          </div>

          <!-- Total Cost -->
          <p class="text-[#00F0FF] font-bold text-lg mt-4">
            {{ $t('Modal.RaffleEntry.Total') }} {{ totalCost }} {{ totalCost > 1 ? $t('Modal.RaffleEntry.Points') : $t('Modal.RaffleEntry.Point') }}
          </p>

          <!-- Action Buttons -->
          <div class="flex w-full gap-4 mt-6">
            <button
              @click="confirmEntry"
              :disabled="loading"
              class="w-1/2 px-4 py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#1ED6FF] transition-colors"
            >
              {{ $t('Modal.RaffleEntry.JoinNow') }}
            </button>
            <button
              @click="$emit('closeRaffleModal')"
              :disabled="loading"
              class="w-1/2 px-4 py-3 bg-[#1A1D24] border border-white/5 text-white font-bold rounded-xl hover:bg-[#2A2E38] transition-colors"
            >
              {{ $t('Modal.RaffleEntry.Cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    showRaffleModal: Boolean,
    raffleName: String,
    entryCost: Number,
    loading: Boolean,
  },
  data() {
    return {
      entries: 1,
    };
  },
  watch: {
    showRaffleModal(newValue) {
      if (!newValue) {
        this.entries = 1;
      }
    },
  },
  computed: {
    totalCost() {
      return this.entries * this.entryCost;
    },
    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },
  },
  methods: {
    increaseEntries() {
      this.entries++;
    },
    decreaseEntries() {
      if (this.entries > 1) this.entries--;
    },
    confirmEntry() {
      this.$emit("confirmEntry", this.entries);
    },
    handleLogin() {
      const loginUrl = `${process.env.VUE_APP_BASE_API}/api/auth/login/kick`;
      window.location.href = loginUrl;
    },
  },
};
</script>

<style scoped>

@keyframes twist-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.twist-spinner {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 3px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-small {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-big {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 4px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

</style>
